// scene-slot-rules.ts — operator-UI §4b: the animated scene slot's RULES, pure and React-free, so every copy of the slot
// (operator, customer, @vms/creation-ui) and a test at the far end of the filmstrip chain call the SAME function.
import type { FilmstripBeat } from "../../api/ops";

export type Slot =
  | { kind: "clip"; url: string; poster: string | null }
  | { kind: "presenter"; image: string }
  | { kind: "card"; url: string }
  | { kind: "crossfade"; start: string; end: string }
  | { kind: "frame"; url: string }
  | { kind: "none" };

const urlOr = (a?: string | null, b?: string | null) => a || b || null;

/** Pure: what this scene's slot shows. A clip wins (it is the scene); a presenter scene sways on its image; a card is the
 *  card; start + end frames crossfade with a push-in; a lone frame pushes in. */
export function slotOf(b: FilmstripBeat): Slot {
  const p = b.production;
  const clip = p?.clip?.url;
  if (clip) return { kind: "clip", url: clip, poster: b.previews.clip?.posterUrl ?? null };
  const start = urlOr(b.previews.frame?.posterUrl, p?.image?.url);
  if (b.shotType === "presenter" && start) return { kind: "presenter", image: start };
  const card = urlOr(b.previews.card?.posterUrl, p?.card?.url);
  if (!start && card) return { kind: "card", url: card };
  const end = urlOr(b.previews.endFrame?.posterUrl, p?.endImage?.url);
  if (start && end) return { kind: "crossfade", start, end };
  if (start) return { kind: "frame", url: start };
  if (card) return { kind: "card", url: card };
  return { kind: "none" };
}

/** Pure: each scene's window in the rough cut — the conformed window when the voice is conformed, else its slice of the
 *  voice track (what the animatic plays). Scenes with neither are left out. */
export function windowsOf(beats: readonly FilmstripBeat[]): { beatId: string; startMs: number; endMs: number }[] {
  return beats.flatMap((b) => {
    const p = b.production;
    if (p?.conformedStartMs != null && p.conformedEndMs != null && p.conformedEndMs > p.conformedStartMs)
      return [{ beatId: b.beatId, startMs: p.conformedStartMs, endMs: p.conformedEndMs }];
    if (p?.sliceStartMs != null && p.sliceMs != null && p.sliceMs > 0)
      return [{ beatId: b.beatId, startMs: p.sliceStartMs, endMs: p.sliceStartMs + p.sliceMs }];
    return [];
  });
}

/** Pure: the scene playing at `ms` (start inclusive, end exclusive), or null. */
export function beatAt(windows: readonly { beatId: string; startMs: number; endMs: number }[], ms: number): string | null {
  return windows.find((w) => ms >= w.startMs && ms < w.endMs)?.beatId ?? null;
}

